import { useState } from 'react'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material'

import { SUPPLIER_ACTIVATION_CHART_HEIGHT } from 'components/reports/Metrics/constants/SupplierActivation'
import { SupplierActivationPointDto } from 'generated/graphql'

import { LegendItem } from '../components/MetricCard/LegendItem'
import { ViewDataButton } from '../components/ViewDataButton'
import { SupplierActivationCurveChart } from './SupplierActivationCurveChart'
import { SupplierActivationDataTable } from './SupplierActivationDataTable'

interface Props {
  title: string
  subtitle: string
  info: string
  points: SupplierActivationPointDto[]
  color: string
  milestoneVerb: string
  loading?: boolean
}

export const SupplierActivationCurveCard = ({
  title,
  subtitle,
  info,
  points,
  color,
  milestoneVerb,
  loading,
}: Props) => {
  const [showingData, setShowingData] = useState(false)

  return (
    <Paper
      variant='outlined'
      sx={{ p: 2.5, height: '100%', borderRadius: 2, borderTop: `3px solid ${color}` }}>
      <Stack direction='row' spacing={1} alignItems='center' mb={0.5}>
        <Typography variant='subtitle1' fontWeight={600}>
          {title}
        </Typography>
        <Tooltip title={info}>
          <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
        </Tooltip>

        <Box sx={{ flexGrow: 1 }} />

        <ViewDataButton
          showingData={showingData}
          onToggle={() => setShowingData((previous) => !previous)}
        />
      </Stack>

      <Typography variant='body2' color='primary.main' mb={2}>
        {subtitle}
      </Typography>

      {loading ? (
        <Skeleton variant='rectangular' height={SUPPLIER_ACTIVATION_CHART_HEIGHT} />
      ) : showingData ? (
        <SupplierActivationDataTable points={points} />
      ) : (
        <Box sx={{ position: 'relative' }}>
          <SupplierActivationCurveChart
            points={points}
            color={color}
            milestoneVerb={milestoneVerb}
          />

          {points.length === 0 && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}>
              <Typography
                variant='body2'
                color='text.secondary'
                textAlign='center'
                sx={{
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  maxWidth: '60%',
                }}>
                No driver in this cohort has had long enough for an outcome to be observed.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {!showingData && (
        <Stack direction='row' spacing={2} mt={1} justifyContent='center'>
          <LegendItem label='Cumulative % of drivers' swatch='line' color={color} />
        </Stack>
      )}
    </Paper>
  )
}
